import React, { useEffect, useRef, useState } from 'react';
import MinusIcon from '@mui/icons-material/RemoveCircleOutline';
import PlusIcon from '@mui/icons-material/AddCircleOutline';
import { useSnackbar } from 'material-ui-snackbar-provider';
import Resizer from 'react-image-file-resizer';
import AvatarEditor from 'react-avatar-editor';
import Slider from '@mui/material/Slider';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import cx from 'classnames';

import UploadFileIcon from '../../../assets/icons/upload-icon-file.svg';
import FolderIcon from '../../../assets/icons/folder-open.svg';
import ShareIcon from '../../../assets/icons/share.svg';
import StorageIcon from '../../../assets/icons/storage.svg';
import { MoreIcon } from '../../../assets/icons/threedots';
import littleMountain from '../../../assets/icons/little-mountain.svg';
import { saveMyOrganization } from '../../../store/user/userSlice';
import bigMountain from '../../../assets/icons/big-mountain.svg';
import { uploadOrgAvatar } from '../../../api/organizationsApi';
import { ContainedButton, OutlinedButton } from '../Buttons';
import { useAppDispatch } from '../../../store/hooks';
import { NOTIFICATION } from '../../../enums/ENUMS';
import styles from './PhotoUpload.module.scss';

type TPhotoUpload = {
  name?: string;
  setCropAvatar: (boolean: boolean) => void;
  setIsFileUploading: (boolean: boolean) => void;
  setOpen: (bool: boolean) => void;
  onUpload?: (data: FormData) => Promise<any>;
  onUploaded?: (data: any) => void;
  saveLabel?: string;
  cropMode?: 'avatar' | 'square' | 'landscape';
  preview?: {
    type: 'folder' | 'file';
    title: string;
    imageUrl?: string;
  };
};

const PhotoUpload: React.FC<TPhotoUpload> = ({
  name,
  setCropAvatar,
  setIsFileUploading,
  setOpen,
  onUpload,
  onUploaded,
  saveLabel = 'Save avatar',
  cropMode = 'avatar',
  preview,
}) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [avatarUrl, setAvatarUrl] = useState(UploadFileIcon);
  const [scale, setScale] = useState<number>(1);
  const [minScale, setMinScale] = useState<number>(1);
  const [previewImageUrl, setPreviewImageUrl] = useState(preview?.imageUrl || '');
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const editorRef = useRef<AvatarEditor>(null);
  const notification = useSnackbar();
  const dragRef = useRef<HTMLDivElement>(null);
  const isThumbnail = cropMode !== 'avatar';
  const editorWidth = isThumbnail ? Math.min(cropMode === 'landscape' ? 480 : 360, window.innerWidth - 96) : 300;
  const editorHeight = cropMode === 'landscape' ? Math.round((editorWidth * 9) / 16) : editorWidth;

  useEffect(() => {
    if (!isFileUploaded) setPreviewImageUrl(preview?.imageUrl || '');
  }, [isFileUploaded, preview?.imageUrl]);

  const loadImage = (file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.src = reader.result as string;
      img.onload = () => {
        const imageAspect = img.naturalWidth / img.naturalHeight;
        const canvasAspect = editorWidth / editorHeight;
        const fitScale = isThumbnail
          ? Math.min(1, imageAspect > canvasAspect ? canvasAspect / imageAspect : imageAspect / canvasAspect)
          : 1;
        setAvatarUrl(img.src);
        setPreviewImageUrl(img.src);
        setMinScale(fitScale);
        setScale(fitScale);
        setIsFileUploaded(true);
        setCropAvatar(true);
      };
    };
    reader.readAsDataURL(file);
  };

  const onDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    loadImage(e.dataTransfer.files[0]);
  };

  const onDragOver = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  const openPhotoUploader = () => {
    inputRef?.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    loadImage(event.target.files?.[0]);
  };

  const updatePreview = () => {
    const canvas = editorRef.current?.getImageScaledToCanvas();
    if (canvas) setPreviewImageUrl(canvas.toDataURL(isThumbnail ? 'image/png' : 'image/jpeg', 0.8));
  };

  const handleSave = async () => {
    if (editorRef.current) {
      try {
        const canvas = editorRef.current.getImage();
        const preserveTransparency = isThumbnail;
        const outputFormat = preserveTransparency ? 'PNG' : 'JPEG';
        const dataUrl = canvas.toDataURL(preserveTransparency ? 'image/png' : 'image/jpeg');

        setIsFileUploading(true);

        const blob = await fetch(dataUrl).then((res) => res.blob());

        const compressedResult = await new Promise<Blob>((resolve) => {
          Resizer.imageFileResizer(
            blob,
            cropMode === 'landscape' ? 640 : 300,
            cropMode === 'landscape' ? 360 : 300,
            outputFormat,
            preserveTransparency ? 100 : 80,
            0,
            (uri) => {
              const resultBlob = fetch(uri as string).then((res) => res.blob());
              resolve(resultBlob);
            },
            'base64',
          );
        });

        const formData = new FormData();
        formData.append('file', compressedResult, `compressed-image.${preserveTransparency ? 'png' : 'jpg'}`);
        const res = onUpload ? await onUpload(formData) : await uploadOrgAvatar(formData);
        if (onUpload) {
          onUploaded?.(res.data);
        } else {
          dispatch(saveMyOrganization(res.data));
        }
        notification.showMessage(NOTIFICATION.FILE_UPLOADED);
      } catch (err) {
        notification.showMessage(NOTIFICATION.FILE_NOT_UPLOADED);
      } finally {
        setIsFileUploading(false);
        setOpen(false);
      }
    }
  };

  const handleScale = (event: Event, value: number | number[]) => {
    setScale(value as number);
  };

  const cancel = () => {
    setIsFileUploaded(false);
    setCropAvatar(false);
    setPreviewImageUrl(preview?.imageUrl || '');
  };

  const recommendation = cropMode === 'landscape'
    ? 'Best result: 1280 × 720 px (16:9). Other sizes can be centred and fitted without cropping.'
    : 'Square images use the available icon space best. Other aspect ratios are supported and will be centred without cropping.';

  const content = !isFileUploaded ? (
    <div
      ref={dragRef}
      onClick={openPhotoUploader}
      onDrop={onDrop}
      onDragOver={onDragOver}
      className={cx(styles.photoUpload, isThumbnail && styles.thumbnailUpload)}
    >
      {name ? (
        <>
          <div className={styles.text}>{name}</div>
        </>
      ) : (
        <>
          <input hidden type="file" ref={inputRef} onChange={handleFileChange} size={5e10} accept="image/png, image/jpeg" />

          <img src={UploadFileIcon} alt="upload content " />

          <div>
            Upload image by drag and drop or <span className={styles.text}> click to upload</span>
          </div>
          {isThumbnail && <div className={styles.recommendation}>{recommendation}</div>}
        </>
      )}
    </div>
  ) : (
    <div className={cx(isThumbnail && styles.thumbnailEditor)}>
      <div>
        <AvatarEditor
          ref={editorRef}
          image={avatarUrl}
          width={editorWidth}
          height={editorHeight}
          scale={scale}
          border={isThumbnail ? 0 : 25}
          borderRadius={cropMode === 'avatar' ? 400 : 0}
          disableBoundaryChecks={isThumbnail}
          onImageReady={updatePreview}
          onImageChange={updatePreview}
        />

        {isThumbnail && <div className={styles.recommendation}>{recommendation}</div>}

        <Box>
          <div className={styles.slider}>
            <IconButton
              type="button"
              size="small"
              color="inherit"
              aria-label="Zoom image out"
              disabled={scale <= minScale}
              onClick={() => setScale((current) => Math.max(minScale, current - 0.1))}
            >
              <MinusIcon fontSize="small" />
            </IconButton>
            <Slider
              sx={{
                '& .MuiSlider-thumb': {
                  color: '#39B3EA',
                },
                '& .MuiSlider-track': {
                  color: '#39b3ea',
                },
                '& .MuiSlider-rail': {
                  color: '#acc4e4',
                },
                '& .MuiSlider-active': {
                  color: 'green',
                },
              }}
              value={scale}
              min={minScale}
              max={20}
              step={0.01}
              aria-label="Image zoom"
              valueLabelDisplay="auto"
              onChange={handleScale}
            />
            <IconButton
              type="button"
              size="small"
              color="inherit"
              aria-label="Zoom image in"
              disabled={scale >= 20}
              onClick={() => setScale((current) => Math.min(20, current + 0.1))}
            >
              <PlusIcon fontSize="small" />
            </IconButton>
          </div>
        </Box>
      </div>
    </div>
  );

  return (
    <div className={cx(isThumbnail && styles.thumbnailLayout)}>
      {content}
      {preview && (
        <div className={styles.previewPanel}>
          <div className={styles.previewLabel}>List preview</div>
          <div className={styles.previewScroll}>
            <div className={cx(styles.previewGrid, preview.type === 'folder' ? styles.folderGrid : styles.fileGrid)}>
              <div>Name</div>
              <div>Share</div>
              {preview.type === 'file' && <div>Status</div>}
              <div>Created At</div>
              <div>File Size</div>
              <div>Upload Hours</div>
              <div>Actions</div>
            </div>
            <div
              className={cx(
                styles.previewGrid,
                styles.previewRow,
                preview.type === 'folder' ? styles.folderGrid : styles.fileGrid,
              )}
            >
              <div className={styles.previewName}>
                <div className={cx(styles.previewImage, preview.type === 'folder' && styles.folderPreview)}>
                  <img src={previewImageUrl || (preview.type === 'folder' ? FolderIcon : UploadFileIcon)} alt="preview" />
                </div>
                <div className={styles.previewTitle}>{preview.title}</div>
              </div>
              <div className={styles.previewShare}>
                <img src={ShareIcon} alt="share" />
              </div>
              {preview.type === 'file' && <div className={styles.previewStatus}>Uploaded</div>}
              <div>Preview</div>
              <div className={styles.previewSize}>
                <img src={StorageIcon} alt="size" /> —
              </div>
              <div>—</div>
              <div className={styles.previewActions}>
                <MoreIcon />
              </div>
            </div>
          </div>
        </div>
      )}
      {isFileUploaded && (
        <div className={styles.flex}>
          <OutlinedButton kind="default" onClick={cancel}>
            Cancel
          </OutlinedButton>
          <ContainedButton onClick={handleSave}>{saveLabel}</ContainedButton>
        </div>
      )}
    </div>
  );
};
export default PhotoUpload;
